import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getAccount, useAuthSession } from '@/api';
import { betslipReplace } from '@/betslip/betslip-store';
import { useBetslip } from '@/betslip/useBetslip';
import { useBetslipLiveSync } from '@/betslip/useBetslipLiveSync';
import { useBetslipRewards } from '@/betslip/useBetslipRewards';
import { useKreditBet } from '@/betslip/useKreditBet';
import { useMyBets } from '@/betslip/useMyBets';
import { usePlaceBet, type PlaceBetSuccess } from '@/betslip/usePlaceBet';
import { useReuseBet } from '@/betslip/useReuseBet';
import { kreditBetMaxStake } from '@/domain/kredit-bet';
import { resolveCombiBonus } from '@/domain/combi-bonus';
import {
  removeCustomPackageFromTicket,
  ticketForKeepBets,
} from '@/betslip/ticket-success';
import { getUserSettings, updateUserRewards } from '@/api/auth';
import {
  appliedRewardFromTicket,
  removeRewardById,
  rewardDisplayName,
} from '@/domain/rewards';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { BET_BUILDER_MIN_SELECTIONS } from '@/domain/bet-builder';
import {
  countBets,
  flattenBetslipSelections,
  removeSelection,
  type BetslipSelectionKeyParts,
  type BetslipTicket,
  type FlattenedBetslipSelection,
} from '@/domain/betslip';
import { removeCustomSelection } from '@/domain/custom-bets';
import { formatOutcomeLabel } from '@/domain/event-details';
import { outcomeFlashKey } from '@/domain/live-odds';
import { formatOddForDisplay } from '@/domain/odds-format';
import type { PlaceBetMessage } from '@/domain/place-bet';
import { formatAmount } from '@/domain/player-balance';
import { stripMarketPlaceholders, translateMarketLabel } from '@/i18n/market-labels';
import { translateOutcomeLabel } from '@/i18n/outcome-labels';
import { translatePeriodLabel } from '@/i18n/period-label';
import { useLiveFeedRevision, useOptionalLiveFeed } from '@/live/LiveFeedProvider';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/common/button';
import { Toast } from '@/ui/common/toast';
import { BETSLIP_MODES, type BetslipMode } from '@/ui/betslip/Betslip.modes';
import { BetslipCustomPackageRow } from '@/ui/betslip/BetslipCustomPackageRow';
import { CombiBonusBanner } from '@/ui/betslip/CombiBonusBanner';
import { BetslipSelectionRow } from '@/ui/betslip/BetslipSelectionRow';
import { ApplyRewardBanner } from '@/ui/betslip/ApplyRewardBanner';
import { AvailableRewardsSheet } from '@/ui/betslip/AvailableRewardsSheet';
import { BetslipStakeBar, BETSLIP_QUICK_STAKES } from '@/ui/betslip/BetslipStakeBar';
import { BetslipSuccess } from '@/ui/betslip/BetslipSuccess';
import { BetslipSystemOptions } from '@/ui/betslip/BetslipSystemOptions';
import { BetslipTotals } from '@/ui/betslip/BetslipTotals';
import type { BetslipPage } from '@/ui/betslip/EmptyBetslip';
import { KreditBetBanner } from '@/ui/betslip/KreditBetBanner';
import { KreditBetConnectedBar } from '@/ui/betslip/KreditBetConnectedBar';
import { KreditBetSelectHeader } from '@/ui/betslip/KreditBetSelectHeader';
import { KreditBetSelectedTicket } from '@/ui/betslip/KreditBetSelectedTicket';
import { KreditBetTicketCard } from '@/ui/betslip/KreditBetTicketCard';
import { MyBetsPanel } from '@/ui/betslip/MyBetsPanel';

interface PlacementSuccessState extends PlaceBetSuccess {
  keepBets: boolean;
}

export interface BetslipPanelProps {
  className?: string;
}

function CountBadge({ label }: { label: string }) {
  return (
    <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-action-primary px-1 text-[10px] font-bold text-text-on-action">
      {label}
    </span>
  );
}

function currencyCode(userCurrency: string | undefined): string {
  return userCurrency?.trim() || '';
}

function withCurrencyPrefix(amount: string, currency: string): string {
  return currency ? `${currency}${amount}` : amount;
}

function formatPlaceBetMessage(
  t: (key: string, options?: { defaultValue?: string }) => string,
  message: PlaceBetMessage,
  currency: string,
): string {
  const label = t(message.key, { defaultValue: message.defaultValue });
  if (message.limit == null) return label;
  if (message.limitKind === 'odds') {
    return `${label} ${formatOddForDisplay(message.limit)}.`;
  }
  if (message.limitKind === 'count') {
    return `${label} ${message.limit}.`;
  }
  return `${label} ${withCurrencyPrefix(formatAmount(message.limit), currency)}.`;
}

function selectionLabels(
  row: FlattenedBetslipSelection,
  resolveMarketLabel: (key: string) => string,
  resolveOutcomeLabel: (code: string, specifier?: string | null) => string,
): { selection: string; market: string } {
  if (row.outright) {
    const fromSpecifier = row.specifier ? resolveMarketLabel(row.specifier) : '';
    const fromMarket = row.marketId ? resolveMarketLabel(String(row.marketId)) : '';
    const market =
      fromSpecifier && fromSpecifier !== row.specifier
        ? fromSpecifier
        : fromMarket && fromMarket !== String(row.marketId)
          ? fromMarket
          : row.marketLabel;
    return { selection: row.outcomeName, market };
  }

  const marketKey = row.marketId ?? row.marketLabel;
  return {
    selection: row.outcome
      ? resolveOutcomeLabel(row.outcome, row.specifier)
      : row.outcomeName,
    market: marketKey ? resolveMarketLabel(String(marketKey)) : row.marketLabel,
  };
}

/**
 * Connected betslip for the desktop rail and mobile `/betslip` route.
 */
export function BetslipPanel({ className }: BetslipPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useBetslipLiveSync();
  const liveFeed = useOptionalLiveFeed();
  const { returnPath, pathname, routeTab } = useRouterState({
    select: (state) => {
      const { pathname: path, searchStr, search } = state.location;
      const tab = (search as { tab?: string }).tab === 'my-bets' ? 'my-bets' : 'betslip';
      return {
        returnPath: `${path}${searchStr}`,
        pathname: path,
        routeTab: path === '/betslip' ? (tab as BetslipPage) : null,
      };
    },
  });
  const { loggedIn, user } = useAuthSession();
  const feedType = getAccount()?.FeedType;
  const language = i18n.resolvedLanguage ?? i18n.language;
  const {
    ticket,
    mode,
    selectionCount,
    selections,
    maxOdds,
    possibleWin,
    stake,
    stakePerBet,
    systemMin,
    systemMax,
    systemLimits,
    canUseSystem,
    canBank,
    remove,
    removeCustomPackage,
    removeCustomLeg,
    setStake,
    setMode,
    setSystemMin,
    toggleBanked,
    possibleWinPending,
  } = useBetslip();
  const kreditBet = useKreditBet();
  const rewards = useBetslipRewards();
  /** Matches AppShell aside (`xl`) — desktop rail shows rewards inline. */
  const isDesktopRail = useMediaQuery('(min-width: 1280px)');
  const slipEventIds = useMemo(
    () =>
      selections
        .map((row) => (typeof row.eventId === 'number' ? row.eventId : Number(row.eventId)))
        .filter(Number.isFinite),
    [selections],
  );
  useLiveFeedRevision(slipEventIds);
  const { placeBet, isPending, reset: resetPlaceBet, errorMessage } = usePlaceBet();
  const [placementSuccess, setPlacementSuccess] = useState<PlacementSuccessState | null>(null);

  const resolveMarketLabel = useMemo(() => {
    void language;
    // Specifier stays on the selection line (e.g. Over (2.5)); market is just the name.
    return (marketKey: string) =>
      stripMarketPlaceholders(translateMarketLabel(marketKey, i18n, feedType));
  }, [feedType, i18n, language]);

  const resolveOutcomeLabel = useMemo(() => {
    void language;
    return (code: string, specifier?: string | null) =>
      formatOutcomeLabel(translateOutcomeLabel(code, i18n, feedType), specifier ?? undefined);
  }, [feedType, i18n, language]);

  const resolvePeriodLabel = useMemo(() => {
    void language;
    return (key: string) => translatePeriodLabel(key, t, i18n);
  }, [i18n, language, t]);

  const [page, setPageState] = useState<BetslipPage>(routeTab ?? 'betslip');
  const currency = currencyCode(user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode);
  const combiBonus = useMemo(
    () => (loggedIn && mode === 'combi' ? resolveCombiBonus(ticket, user?.Bonuses) : null),
    [loggedIn, mode, ticket, user?.Bonuses],
  );
  const displayPossibleWin =
    combiBonus?.kind === 'applied' ? possibleWin + combiBonus.bonusValue : possibleWin;
  const activePage = !loggedIn && page === 'my-bets' ? 'betslip' : page;
  const myBets = useMyBets(loggedIn);
  const prevSelectionCountRef = useRef(selectionCount);

  const setPage = (next: BetslipPage) => {
    setPageState(next);
    if (pathname !== '/betslip') return;
    void navigate({
      to: '/betslip',
      search: next === 'my-bets' ? { tab: 'my-bets' } : {},
      replace: true,
    });
  };

  const {
    reuseBet,
    isPending: reusePending,
    errorMessage: reuseError,
    clearError: clearReuseError,
    pendingTicketId,
  } = useReuseBet({
    onSuccess: () => {
      setPage('betslip');
      setPlacementSuccess(null);
      resetPlaceBet();
    },
  });

  useEffect(() => {
    if (routeTab && routeTab !== page) setPageState(routeTab);
  }, [routeTab, page]);

  useEffect(() => {
    if (loggedIn || page !== 'my-bets') return;
    setPageState('betslip');
    if (pathname === '/betslip') {
      void navigate({ to: '/betslip', search: {}, replace: true });
    }
  }, [loggedIn, page, pathname, navigate]);

  // Desktop rail: odd clicks stay on the sports page, so jump from My bets to Bet slip
  // when a selection is added. Mobile `/betslip` keeps the chosen tab.
  useEffect(() => {
    const prev = prevSelectionCountRef.current;
    prevSelectionCountRef.current = selectionCount;
    if (selectionCount <= prev) return;
    if (activePage !== 'my-bets') return;
    if (pathname === '/betslip') return;
    setPageState('betslip');
  }, [selectionCount, activePage, pathname]);

  const stakeLabel = formatAmount(stake);
  const stakePerBetLabel = formatAmount(
    stakePerBet ?? (selectionCount > 0 ? stake / selectionCount : 0),
  );
  const selectedQuick =
    BETSLIP_QUICK_STAKES.find((value) => Math.abs(value - stake) < 0.001) ?? null;

  const pageTabs: { value: BetslipPage; label: string; count: number }[] = [
    {
      value: 'betslip',
      label: t('Betslip', { defaultValue: 'Bet Slip' }),
      count: selectionCount,
    },
    ...(loggedIn
      ? [
          {
            value: 'my-bets' as const,
            label: t('MyBets', { defaultValue: 'My bets' }),
            count: myBets.openCount,
          },
        ]
      : []),
  ];

  const handleMode = (next: BetslipMode) => {
    if (next === 'system' && !canUseSystem) return;
    setMode(next);
  };

  const handlePlaceBet = () => {
    if (!loggedIn) {
      // Desktop: return to the sports/live page that hosts the rail.
      // Mobile `/betslip`: returnPath is already `/betslip`.
      void navigate({
        to: '/login',
        search: { redirect: returnPath.startsWith('/') ? returnPath : '/betslip' },
      });
      return;
    }
    resetPlaceBet();
    void placeBet()
      .then((result) => {
        const usedRewardId = result.ticket.RewardId;
        if (typeof usedRewardId === 'number') {
          const session = getUserSettings();
          if (session) {
            updateUserRewards(removeRewardById(session.Rewards, usedRewardId));
          }
        }
        setPlacementSuccess({ ...result, keepBets: false });
      })
      .catch(() => {
        // Mapped message is exposed via errorMessage.
      });
  };

  const handleContinueAfterSuccess = () => {
    if (
      placementSuccess?.keepBets &&
      placementSuccess.ticket.Items.length > 0
    ) {
      betslipReplace(ticketForKeepBets(placementSuccess.ticket));
    }
    setPlacementSuccess(null);
    resetPlaceBet();
  };

  const updateSuccessTicket = (next: BetslipTicket) => {
    setPlacementSuccess((prev) => (prev ? { ...prev, ticket: next } : prev));
  };

  const handleSuccessRemove = (key: BetslipSelectionKeyParts) => {
    if (!placementSuccess) return;
    updateSuccessTicket(removeSelection(placementSuccess.ticket, key));
  };

  const handleSuccessRemoveCustomPackage = (eventId: string | number) => {
    if (!placementSuccess) return;
    updateSuccessTicket(removeCustomPackageFromTicket(placementSuccess.ticket, eventId));
  };

  const handleSuccessRemoveCustomLeg = (eventId: string | number, legKey: string) => {
    if (!placementSuccess) return;
    updateSuccessTicket(removeCustomSelection(placementSuccess.ticket, eventId, legKey));
  };

  const successSelections = useMemo(
    () => (placementSuccess ? flattenBetslipSelections(placementSuccess.ticket) : []),
    [placementSuccess],
  );
  const successTips = placementSuccess ? countBets(placementSuccess.ticket) : 0;
  const successStakeLabel = placementSuccess
    ? withCurrencyPrefix(formatAmount(placementSuccess.ticket.Stake), currency)
    : '';
  const successMaxOddsLabel = placementSuccess
    ? formatOddForDisplay(placementSuccess.ticket.Odds ?? 0)
    : '';
  const successPossibleWinLabel = placementSuccess
    ? withCurrencyPrefix(formatAmount(placementSuccess.ticket.PossibleWin ?? 0), currency)
    : '';
  const successRewardName = (() => {
    if (!placementSuccess) return null;
    const used = appliedRewardFromTicket(placementSuccess.ticket);
    return used ? rewardDisplayName(used, formatAmount, currency) : null;
  })();

  const handleStakeInput = (raw: string) => {
    const cleaned = raw.replace(/,/g, '').match(/^\d*\.?\d{0,2}/)?.[0];
    if (cleaned === undefined || cleaned === '') {
      setStake(0);
      return;
    }
    setStake(Number.parseFloat(cleaned) || 0);
  };

  const handleMax = () => {
    const kreditMax = kreditBetMaxStake(ticket);
    if (kreditMax != null) {
      setStake(Math.floor(kreditMax * 100) / 100);
      return;
    }
    const balance =
      (user?.Credits?.WithdrawableCredits ?? 0) + (user?.Credits?.RestrictedCredits ?? 0);
    if (balance > 0) setStake(Math.floor(balance * 100) / 100);
  };

  const handleSingleStakeChange = (raw: string) => {
    const cleaned = raw.replace(/,/g, '').match(/^\d*\.?\d{0,2}/)?.[0];
    const perBet = cleaned === undefined || cleaned === '' ? 0 : Number.parseFloat(cleaned) || 0;
    setStake(perBet * Math.max(selectionCount, 1));
  };

  const empty = selectionCount === 0;
  const hasBlockedSelection = selections.some((row) => Boolean(row.blocked));
  const hasIncompleteCustom = selections.some(
    (row) =>
      row.custom === true &&
      (row.customSelectionCount ?? 0) < BET_BUILDER_MIN_SELECTIONS,
  );
  const placeDisabled =
    hasBlockedSelection || hasIncompleteCustom || possibleWinPending;

  return (
    <section
      aria-label={t('Betslip', { defaultValue: 'Bet Slip' })}
      className={cn(
        // Fill the host (mobile page or desktop aside) so the footer stays pinned
        // while only the selections list scrolls.
        'flex h-full min-h-0 w-full flex-col gap-2',
        className,
      )}
    >
      <div
        role="tablist"
        aria-label={t('BetslipPages', { defaultValue: 'Bet slip pages' })}
        className="flex h-9 shrink-0 items-end justify-center"
      >
        {pageTabs.map((tab) => {
          const active = activePage === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPage(tab.value)}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 overflow-hidden pt-1.5"
            >
              <span className="flex items-center justify-center gap-1.5">
                <span
                  className={cn(
                    'whitespace-nowrap text-body-m',
                    active ? 'font-bold text-text-primary' : 'font-normal text-text-secondary',
                  )}
                >
                  {tab.label}
                </span>
                {tab.count > 0 ? <CountBadge label={String(tab.count)} /> : null}
              </span>
              <span
                aria-hidden
                className={cn('h-0.5 w-full', active ? 'bg-action-primary' : 'bg-transparent')}
              />
            </button>
          );
        })}
      </div>

      {activePage === 'my-bets' ? (
        <MyBetsPanel
          myBets={myBets}
          currency={currency}
          onReuse={(ticketId) => {
            void reuseBet(ticketId);
          }}
          reusePendingTicketId={reusePending ? pendingTicketId : null}
          reuseError={reuseError}
          onDismissReuseError={clearReuseError}
        />
      ) : placementSuccess ? (
        <BetslipSuccess
          ticketNumber={placementSuccess.ticketNumber}
          placedAt={placementSuccess.placedAt}
          tips={successTips}
          stakeLabel={successStakeLabel}
          maxOddsLabel={successMaxOddsLabel}
          possibleWinLabel={successPossibleWinLabel}
          rewardName={successRewardName}
          keepBets={placementSuccess.keepBets}
          onKeepBetsChange={(keep) =>
            setPlacementSuccess((prev) => (prev ? { ...prev, keepBets: keep } : prev))
          }
          onContinue={handleContinueAfterSuccess}
          selectionList={successSelections.map((row) => {
            if (row.custom) {
              return (
                <BetslipCustomPackageRow
                  key={row.key}
                  match={row.matchLabel}
                  odds={row.odds}
                  legs={row.customLegs ?? []}
                  resolveMarketLabel={resolveMarketLabel}
                  onRemove={() => handleSuccessRemoveCustomPackage(row.eventId)}
                  onRemoveLeg={(legKey) => handleSuccessRemoveCustomLeg(row.eventId, legKey)}
                />
              );
            }

            const labels = selectionLabels(row, resolveMarketLabel, resolveOutcomeLabel);

            return (
              <BetslipSelectionRow
                key={row.key}
                selection={labels.selection}
                match={row.matchLabel}
                market={labels.market}
                odds={row.odds}
                onRemove={() =>
                  handleSuccessRemove({
                    eventId: row.eventId,
                    marketId: row.marketId,
                    specifier: row.specifier,
                    outcome: row.outcome,
                  })
                }
              />
            );
          })}
        />
      ) : kreditBet.view === 'selecting' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <KreditBetSelectHeader onCancel={kreditBet.exitSelecting} />
          {kreditBet.errorMessage ? (
            <Toast tone="danger" onDismiss={kreditBet.clearError}>
              {formatPlaceBetMessage(t, kreditBet.errorMessage, currency)}
            </Toast>
          ) : null}
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain pe-0.5">
            {kreditBet.creditorTickets.map((creditor) => (
              <KreditBetTicketCard
                key={creditor.Id}
                ticket={creditor}
                currency={currency}
                onSelect={kreditBet.selectCreditor}
                className="shrink-0"
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div
            role="tablist"
            aria-label={t('BetType', { defaultValue: 'Bet type' })}
            className="flex shrink-0 items-start"
          >
            {BETSLIP_MODES.map(({ value, label }) => {
              const active = mode === value;
              const disabled = value === 'system' && !canUseSystem;
              return (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-disabled={disabled || undefined}
                  disabled={disabled || isPending}
                  title={
                    disabled
                      ? t('MinimumBetsSystem', {
                          defaultValue: `Minimum {{count}} selections required for system bets`,
                          count: systemLimits.minSystem,
                        })
                      : undefined
                  }
                  onClick={() => handleMode(value)}
                  className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 overflow-hidden pt-1.5 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <span
                    className={cn(
                      'whitespace-nowrap text-[13px] lowercase',
                      active
                        ? 'font-bold text-text-primary'
                        : 'font-normal text-text-secondary',
                    )}
                  >
                    {label}
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'h-0.5 w-full',
                      active ? 'bg-action-primary' : 'bg-transparent',
                    )}
                  />
                </button>
              );
            })}
          </div>

          {empty ? (
            <p className="w-full text-body-s leading-5 text-text-secondary">
              {t('EmptyBetslipMessage', {
                defaultValue:
                  "You haven't selected any bets yet. Tap odds to add selections.",
              })}
            </p>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto overscroll-contain pe-0.5">
                {combiBonus ? (
                  <CombiBonusBanner bonus={combiBonus} currency={currency} />
                ) : null}
                {selections.map((row) => {
                  if (row.custom) {
                    return (
                      <BetslipCustomPackageRow
                        key={row.key}
                        match={row.matchLabel}
                        odds={row.odds}
                        legs={row.customLegs ?? []}
                        resolveMarketLabel={resolveMarketLabel}
                        onRemove={() => removeCustomPackage(row.eventId)}
                        onRemoveLeg={(legKey) => removeCustomLeg(row.eventId, legKey)}
                      />
                    );
                  }

                  const labels = selectionLabels(row, resolveMarketLabel, resolveOutcomeLabel);
                  const selectionLabel = labels.selection;
                  const bankerAllowed = canBank(row.eventId);
                  const eventIdNum =
                    typeof row.eventId === 'number' ? row.eventId : Number(row.eventId);
                  const flashKey =
                    liveFeed &&
                    row.marketId &&
                    row.outcome &&
                    Number.isFinite(eventIdNum)
                      ? outcomeFlashKey(
                          eventIdNum,
                          row.marketId,
                          row.specifier ?? undefined,
                          row.outcome,
                        )
                      : undefined;
                  const oddsFlash = flashKey
                    ? (liveFeed?.getOutcomeFlash(flashKey) ?? 'none')
                    : 'none';
                  return (
                    <BetslipSelectionRow
                      key={row.key}
                      selection={selectionLabel}
                      match={row.matchLabel}
                      market={labels.market}
                      odds={row.odds}
                      live={row.live}
                      liveLabel={t('Live', { defaultValue: 'Live' })}
                      homeScore={row.homeScore}
                      awayScore={row.awayScore}
                      clock={row.clock}
                      liveClockState={row.liveClockState}
                      translatePeriod={resolvePeriodLabel}
                      flash={oddsFlash}
                      blocked={row.blocked}
                      showStake={mode === 'single'}
                      showBanker={mode === 'system'}
                      banked={row.banked}
                      bankerDisabled={!bankerAllowed}
                      stakeLabel={
                        mode === 'single'
                          ? currency
                            ? `${stakePerBetLabel} ${currency}`
                            : stakePerBetLabel
                          : undefined
                      }
                      onRemove={() =>
                        remove({
                          eventId: row.eventId,
                          marketId: row.marketId,
                          specifier: row.specifier,
                          outcome: row.outcome,
                        })
                      }
                      onStakeChange={handleSingleStakeChange}
                      onBankerToggle={() => toggleBanked(row.eventId)}
                    />
                  );
                })}
              </div>

              <footer
                className={cn(
                  'relative z-20 mt-2 flex shrink-0 flex-col gap-2 border-t border-border-subtle bg-bg-canvas pt-2',
                  // Mobile: small gap above the bottom nav; desktop padding is on the aside.
                  'max-lg:pb-2',
                )}
              >
                {errorMessage ? (
                  <Toast tone="danger" onDismiss={resetPlaceBet}>
                    {formatPlaceBetMessage(t, errorMessage, currency)}
                  </Toast>
                ) : rewards.errorMessage ? (
                  <Toast tone="danger" onDismiss={rewards.clearError}>
                    {formatPlaceBetMessage(t, rewards.errorMessage, currency)}
                  </Toast>
                ) : kreditBet.errorMessage ? (
                  <Toast tone="danger" onDismiss={kreditBet.clearError}>
                    {formatPlaceBetMessage(t, kreditBet.errorMessage, currency)}
                  </Toast>
                ) : hasBlockedSelection ? (
                  <Toast tone="warning">
                    {t('BetslipClosedOdds', {
                      defaultValue:
                        'One or more selections are unavailable. Remove them to place your bet.',
                    })}
                  </Toast>
                ) : hasIncompleteCustom ? (
                  <Toast tone="warning">
                    {t('CustomBetMinSelections', {
                      defaultValue: 'Add at least 2 selections to your Bet Builder',
                    })}
                  </Toast>
                ) : possibleWinPending ? (
                  <Toast tone="warning">
                    {t('CustomBetPricing', {
                      defaultValue: 'Calculating Bet Builder odds…',
                    })}
                  </Toast>
                ) : null}
                {mode === 'system' && systemMin != null && systemMax != null ? (
                  <BetslipSystemOptions
                    value={systemMin}
                    max={systemMax}
                    minSystem={systemLimits.minSystem}
                    onChange={setSystemMin}
                  />
                ) : null}
                <BetslipStakeBar
                  amount={stakeLabel}
                  currencySuffix={currency || undefined}
                  selectedQuickStake={selectedQuick}
                  onAmountChange={handleStakeInput}
                  onQuickStake={setStake}
                  onMax={loggedIn ? handleMax : undefined}
                  disabled={rewards.stakeLocked}
                />
                {rewards.showBanner && !(isDesktopRail && rewards.sheetOpen) ? (
                  <ApplyRewardBanner
                    mode={rewards.mode}
                    availableCount={rewards.availableCount}
                    appliedLabel={rewards.appliedLabel}
                    disabled={rewards.blockedByKreditBet && rewards.mode === 'idle'}
                    onAction={rewards.openSheet}
                  />
                ) : null}
                {isDesktopRail ? (
                  <AvailableRewardsSheet
                    variant="inline"
                    open={rewards.sheetOpen}
                    rewards={rewards.activeRewards}
                    selectedId={rewards.appliedReward?.Id ?? null}
                    currency={currency}
                    onClose={rewards.closeSheet}
                    onApply={rewards.applyFromSheet}
                  />
                ) : null}
                {kreditBet.showBanner ? (
                  <KreditBetBanner
                    loading={kreditBet.isLoadingList}
                    onTry={() => {
                      void kreditBet.tryKreditBet();
                    }}
                  />
                ) : null}
                <BetslipTotals
                  maxOdds={formatOddForDisplay(maxOdds)}
                  possibleWin={withCurrencyPrefix(formatAmount(displayPossibleWin), currency)}
                  combiBonus={
                    combiBonus?.kind === 'applied'
                      ? withCurrencyPrefix(formatAmount(combiBonus.bonusValue), currency)
                      : undefined
                  }
                />
                <Button
                  size="lg"
                  fullWidth
                  loading={isPending}
                  disabled={placeDisabled}
                  onClick={handlePlaceBet}
                >
                  {`${withCurrencyPrefix(formatAmount(stake), currency)} ${t('PlaceBet', { defaultValue: 'Place bet' })}`}
                </Button>
                {kreditBet.connected && ticket.CreditorNumber ? (
                  <>
                    <KreditBetConnectedBar onDismiss={kreditBet.dismissCreditor} />
                    <KreditBetSelectedTicket
                      ticketId={ticket.CreditorNumber}
                      ticketType={ticket.CreditorType}
                      maxKredit={ticket.CreditorPossibleWin ?? 0}
                      currency={currency}
                    />
                  </>
                ) : null}
              </footer>
            </div>
          )}
        </>
      )}

      {!isDesktopRail ? (
        <AvailableRewardsSheet
          variant="modal"
          open={rewards.sheetOpen}
          rewards={rewards.activeRewards}
          selectedId={rewards.appliedReward?.Id ?? null}
          currency={currency}
          onClose={rewards.closeSheet}
          onApply={rewards.applyFromSheet}
        />
      ) : null}
    </section>
  );
}
